import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AnalyticsEvent, InsuranceRejectEventData } from '@typesApp/Analytics'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { analyticsEventSelector, resetInsuranceEvent } from '@redux/slices/analyticsSlice'
import { Logger } from '@utils/logger'
import { useEnvironment } from './useEnvironment'

/**
 * Send analytics events for direct billing (Tealium), including:
 *  1. Insurance panel open
 *  2. Insurance network selection (out of network)
 *  3. Insurance apply success
 *  4. Insurance apply rejected
 */
export const useAnalytics = (): void => {
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'Analytics' })
  const dispatch = useDispatch()
  const event = useSelector(analyticsEventSelector)
  const actionModule = useSelector(connectorActionModuleSelector)

  const sendInsuranceEvent = (payload: AnalyticsEvent) => {
    if (typeof actionModule?.sendAnalyticsEvent === 'function' && payload) {
      actionModule.sendAnalyticsEvent(payload).catch(error => {
        Logger.warn('Error sending insurance event', error)
      })

      dispatch(resetInsuranceEvent())
    }
  }

  const getRejectEvent = (event: InsuranceRejectEventData): InsuranceRejectEventData => {
    const message = event.error ? t(`Error.${event.error}`) : ''
    const { error, ...rest } = event
    return {
      ...rest,
      Error_Message: message,
    }
  }

  useEffect(() => {
    if (!event) return

    switch (event.type) {
      case 'open':
      case 'accept':
        sendInsuranceEvent(event)
        break
      case 'reject':
        sendInsuranceEvent(getRejectEvent(event))
        break
    }
  }, [event])
}
